/*
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import _ from "lodash"
// Type-only: the scheduler decides *when* a recalculation runs and never performs
// one itself. Its payload types are tied to `computeResults`' signature so the
// captured inputs cannot drift out of step with the call they are captured for.
import type { computeResults } from "services/computations"

// AIDEV-NOTE: (#540) rc-slider fires `onChange` once per drag step and every step
// marks the tables stale, so the recalculation effect used to run the full
// synchronous `computeResults` dozens of times per drag, each result discarded
// microseconds later. Only the *trigger* is debounced - never the Redux filter
// state, which stays live so the slider handles and their value labels keep
// tracking the drag.
export const RECALCULATION_DEBOUNCE_MS = 150

// Upper bound on how long a sustained drag can go without a recalculation.
// Without it, a user who drags continuously without ever pausing would see the
// "N retirements simulated with these filters" readout in HistoricalDataFilters
// frozen for the whole gesture, since that count only changes when
// `computeResults` runs. This bounds mid-drag recomputation by elapsed time
// (at most one per maxWait) rather than by step count, which is the actual
// defect; the trailing call still fires afterwards with the final position.
export const RECALCULATION_MAX_WAIT_MS = 500

// Tied to `computeResults`' own signature so the captured inputs cannot drift
// out of step with the call they are captured for.
export type RecalculationInputs = Readonly<{
  userParameters: Parameters<typeof computeResults>[0]
  historicalDataFilters: Parameters<typeof computeResults>[1]
  initialScrsData: Parameters<typeof computeResults>[2]
}>

/**
 * Everything one pass of the recalculation effect can see. `userParameters` and
 * `initialScrsData` are nullable because a pass can happen before either has
 * arrived, and the source-identity bookkeeping runs on those passes too.
 */
export type RecalculationObservation = Readonly<{
  userParameters: Parameters<typeof computeResults>[0] | null
  historicalDataFilters: Parameters<typeof computeResults>[1]
  initialScrsData: Parameters<typeof computeResults>[2] | null
  initialScrsDataReady: boolean
  filtersInitialized: boolean
  uiTablesCalculationPending: boolean
  /**
   * Whether a previous recalculation of these same inputs is already recorded as having
   * thrown (`recalculationFailed` in ResultsSlice). Read only on the first observation
   * after a mount - see the note on that branch in `observeInputs`.
   */
  recalculationFailed: boolean
}>

export interface RecalculationScheduler {
  /**
   * Call once per pass of the recalculation effect - including passes where
   * nothing can be recalculated. The superseded-data detection compares source
   * identities against the *previous* pass, so a skipped pass would make a later
   * change of data look like no change at all.
   */
  readonly observeInputs: (observation: RecalculationObservation) => void
  /**
   * Run any queued trailing recalculation now. Called on unmount; see the
   * AIDEV-NOTE in `useCoalescedRecalculation` for why unmount flushes where
   * superseded data cancels.
   */
  readonly flushPending: () => void
}

// The two *low*-frequency recalculation inputs. Both are plain slice references, so a
// change of identity means a genuinely new submission or a newly fetched SCR table.
// `unknown` because only identity is ever compared, never the contents - and because
// these are recorded before the readiness guard, where either may still be absent.
type SourceIdentities = Readonly<{
  userParameters: unknown
  initialScrsData: unknown
}>

// Where a throw from a flushed recalculation should go. Named rather than boolean so the
// call site says which behaviour it wants: "boundary" lets the throw reach
// SentryErrorBoundary, "deferred" hands it to handleDeferredError.
type FlushRoute = "boundary" | "deferred"

// AIDEV-NOTE: (#540) Behaviour-preserving extraction of the scheduling that previously
// lived inline in Results.tsx as two refs plus a `useState`-held debounce. It is kept
// free of React and of Redux so the temporal contract can be driven directly with fake
// timers - see recalculationScheduler.property.test.ts, which models the invariants that
// the component-level examples in Results.test.tsx can only sample. The cancel/flush
// asymmetry below is the part most likely to be "simplified" wrongly; both directions
// are pinned by that property test.
//
// `handleDeferredError` receives anything `recalculate` throws on an invocation whose
// throw has nowhere useful to go - see `throwsToBoundary` below for which those are, and
// why the distinction has to be made here rather than in the callback. Note that "nowhere
// useful" is not the same as "no synchronous caller": the unmount flush has a caller and
// still routes here, because that caller is a React cleanup mid-navigation.
export const createRecalculationScheduler = (
  recalculate: (inputs: RecalculationInputs) => void,
  handleDeferredError: (error: unknown) => void,
  debounceMs: number = RECALCULATION_DEBOUNCE_MS,
  maxWaitMs: number = RECALCULATION_MAX_WAIT_MS
): RecalculationScheduler => {
  // The inputs the next recalculation should use. Written on every scheduling pass
  // and read back only when the debounced call actually fires, so the trailing
  // recalculation is computed from the filters as they stand at invocation time
  // rather than from whichever step happened to schedule it.
  let pendingInputs: RecalculationInputs | null = null

  // Source identities as of the previous observation, or null before the first one.
  let lastSeenSources: SourceIdentities | null = null

  // AIDEV-NOTE: (#1069, routing revised by #1247 review) True only while an invocation is
  // running whose throw should reach SentryErrorBoundary. lodash reaches
  // `recalculatePendingInputs` from three places and the flush entry point serves two
  // callers, and only the scheduler can tell them apart - which is why the catch lives
  // here rather than in the callback.
  //
  // It is deliberately NOT "a flush is on the stack": `flushPending` issues a flush with
  // this false. Keep the name and this note describing the *route*, not the mechanism,
  // or the next reader will infer the wrong thing from a call site.
  //
  //  - `flush()` from the observing effect body, when new source data supersedes a
  //    pending call: a synchronous caller on a live screen can still see a throw, so it
  //    keeps reaching SentryErrorBoundary and the user gets the Reload Page fallback.
  //    The unmount cleanup deliberately does NOT take this route - see flushSynchronously.
  //    AIDEV-NOTE: (#1251) The first observation after a mount takes this route too, since
  //    it has no previous sources to differ from. That is a page arriving rather than data
  //    superseding a pending call, and the fallback would replace a page the user has just
  //    navigated to - so `observeInputs` returns before reaching here for the one arrival
  //    known to throw, a mount carrying `recalculationFailed`. Any *other* first-observation
  //    throw still tears the page down; that is untouched behaviour, not a claim of safety.
  //  - the trailing-edge `setTimeout`: no caller at all. A throw is unhandled, no error
  //    boundary sees it, and `setResultsTables` never runs - the whole of #1069.
  //  - lodash's own maxWait invocation, which `debounced()` performs *synchronously*
  //    mid-drag when the ceiling has elapsed (lodash.js, `debounced` -> `invokeFunc`).
  //    Synchronous, but still part of a filter gesture rather than of new data arriving,
  //    so it is routed like the timer: tearing the page down mid-drag would be worse
  //    than the toast, and it is the same failure the user is about to see anyway.
  let throwsToBoundary = false

  const recalculatePendingInputs = (): void => {
    const inputs = pendingInputs
    if (inputs === null) return
    pendingInputs = null
    if (throwsToBoundary) {
      recalculate(inputs)
      return
    }
    try {
      recalculate(inputs)
    } catch (error) {
      handleDeferredError(error)
    }
  }

  // Created once per scheduler, which is created once per mount: a debounced function
  // rebuilt mid-drag would silently abandon its pending trailing call along with it.
  const debouncedRecalculate = _.debounce(recalculatePendingInputs, debounceMs, {
    maxWait: maxWaitMs,
  })

  // The only route to `flush()`, so a future call site cannot silently acquire - or
  // silently lose - the deferred-error handling. The route is named at the call site
  // rather than inferred, because the two callers want opposite things from a throw.
  //
  // AIDEV-NOTE: (#1247 review) The unmount cleanup takes "deferred", not "boundary".
  // Routing it to the boundary blanks the page the user has *just navigated to*: drag a
  // filter, click away inside the debounce window, and the cleanup's throw replaces the
  // new page with the full-screen Reload Page fallback, for a calculation belonging to a
  // screen that no longer exists. Nothing is on screen for a toast to contradict by then,
  // so the deferred route reports without destroying an unrelated page.
  //
  // The `finally` restores rather than clears: on the throw this exists to let past, a
  // nested flush must not clear an outer flush's flag and silently downgrade the rest of
  // that stack from the boundary route to the deferred one. Not reachable today - only
  // the unmount cleanup and the source-change path call it, and neither re-enters - but
  // the assignment costs the same either way.
  const flushSynchronously = (route: FlushRoute): void => {
    const previousRoute = throwsToBoundary
    throwsToBoundary = route === "boundary"
    try {
      debouncedRecalculate.flush()
    } finally {
      throwsToBoundary = previousRoute
    }
  }

  const observeInputs = (observation: RecalculationObservation): void => {
    const { userParameters, initialScrsData } = observation
    const previousSources = lastSeenSources
    // A scheduler is created once per mount, so "no previous observation" and "arriving on
    // the page" are the same event. Named separately from `sourcesChanged` below, which
    // subsumes it: the two want opposite things on the failure path.
    const isFirstObservation = previousSources === null
    const sourcesChanged =
      isFirstObservation ||
      previousSources.userParameters !== userParameters ||
      previousSources.initialScrsData !== initialScrsData
    lastSeenSources = { userParameters, initialScrsData }

    // AIDEV-NOTE: (#540) This runs *before* the readiness guard on purpose. A submission
    // computes its own results and stores them itself, in its submissionCommitted dispatch
    // (UserParametersForm.tsx, "const computedUiTables = computeResults(...)"), which
    // marks them fresh - so the observing effect bails at the guard below and never gets
    // to re-point the pending call at the new data. A trailing call queued by a drag a
    // moment earlier would then fire against the superseded parameters and overwrite the
    // newer results, putting the previous submission's figures back on screen as though
    // they were current. Dropping it here is the opposite of the unmount flush and
    // deliberately so: that trailing call is not merely late, it is wrong.
    if (sourcesChanged) {
      debouncedRecalculate.cancel()
      pendingInputs = null
    }

    // AIDEV-NOTE: (#1251) A mount that inherits a recorded failure must schedule nothing.
    // `recalculationFailed` says a recalculation of *these* inputs already threw, and
    // `computeResults` is a pure function of them - so re-running it here can only throw
    // again. It would do so on the worst available route: this is a first observation, so
    // `sourcesChanged` is true, and the flush below would take the boundary route and
    // replace the page the user has just navigated back to with the Reload Page fallback,
    // in place of the banner Results.tsx renders for exactly this state.
    //
    // Scoped to the first observation on purpose. Every later pass carrying the flag is a
    // filter move, which is the retry the banner asks for and must still be scheduled.
    // `lastSeenSources` is already recorded above, so returning here does not make the next
    // pass look like another first observation.
    if (isFirstObservation && observation.recalculationFailed) {
      return
    }

    // Only recalculate if we have all the necessary data and filters are initialized.
    // Deliberately no freshness condition on the SCR data, so a filter change still
    // recalculates.
    if (
      userParameters &&
      initialScrsData &&
      observation.initialScrsDataReady &&
      observation.filtersInitialized &&
      observation.uiTablesCalculationPending
    ) {
      pendingInputs = {
        userParameters,
        historicalDataFilters: observation.historicalDataFilters,
        initialScrsData,
      }
      debouncedRecalculate()
      if (sourcesChanged) {
        // Only the filter-driven trigger fires at drag frequency. New data must not wait
        // out the debounce: deferring it would leave the *previous* calculation's numbers
        // on screen after the user clicked Calculate Results, the same misleading
        // staleness this debounce exists to prevent, reached from the other direction.
        // Schedule-then-flush rather than recalculating directly, so this path runs the
        // same code as every other recalculation.
        flushSynchronously("boundary")
      }
    }
  }

  return {
    observeInputs,
    flushPending: (): void => {
      flushSynchronously("deferred")
    },
  }
}
